import { queryOptions } from "@tanstack/react-query";
import { apiFetch } from "@/shared/api";

export interface Command {
  /** Slug — the trigger after `/`. Stable; system commands keep this as id. */
  id: string;
  /** UUID for personal commands (DB row); null for system (file-based). */
  uuid: string | null;
  /** Stable cross-scope handle: 'system:<slug>' | 'user:<uuid>'. Used to
   *  match against `Agent.command_refs` for filtering the slash menu. */
  ref: string;
  scope: "system" | "personal";
  label: string;
  description: string;
  template: string;
}

export interface CommandInput {
  /** Human-readable name. Slug is derived server-side from this. */
  label: string;
  description: string;
  template: string;
}

export const commandsQueryOptions = () =>
  queryOptions({
    queryKey: ["commands"],
    queryFn: async (): Promise<Command[]> => {
      const resp = await apiFetch("/api/commands");
      const data: { commands: Command[] } = await resp.json();
      return data.commands ?? [];
    },
    staleTime: Infinity,
  });

/** Commands the *agent* exposes to its slash menu — resolved server-side
 *  with agent-visibility gating, so foreign user-personal commands the
 *  caller doesn't own come back too (the "gateway through agent" model).
 *  `enabled` is false when no agent is selected (hero before pick / new
 *  agent before first save). */
export const agentCommandsQueryOptions = (agentRef: string | null) =>
  queryOptions({
    queryKey: ["agent-commands", agentRef],
    queryFn: async (): Promise<Command[]> => {
      if (!agentRef) return [];
      const resp = await apiFetch(`/api/agents/${agentRef}/commands`);
      const data: { commands: Command[] } = await resp.json();
      return data.commands ?? [];
    },
    enabled: !!agentRef,
    staleTime: Infinity,
  });

/** Expand `$ARGUMENTS` in a command template with user-provided args. */
export function expandCommand(template: string, args: string): string {
  return template.replace(/\$ARGUMENTS/g, args).trim();
}

export async function createCommand(input: CommandInput): Promise<Command> {
  const resp = await apiFetch("/api/commands", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}

export async function updateCommand(
  uuid: string,
  input: CommandInput,
): Promise<Command> {
  const resp = await apiFetch(`/api/commands/${uuid}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}

export async function deleteCommand(uuid: string): Promise<void> {
  const resp = await apiFetch(`/api/commands/${uuid}`, { method: "DELETE" });
  if (!resp.ok) throw new Error(await resp.text());
}
